/**
 * Risk score bands: the `{ tag, min, max }` ranges a deal's weekly risk score
 * is classified into, which is what puts the `riskTag` on every risky deal of
 * a `RISK_REPORT`.
 *
 * They are resolved fresh on every read through a three level chain, so both
 * editors that write them live here:
 *   1. the org's own override (Organizations -> Deal Risk Bands)
 *   2. the admin platform default (Global Configs -> Deal Risk Classification)
 *   3. the hardcoded fallback below
 *
 * Tags are free text -- an admin can rename or re-range them -- so nothing
 * keys off a specific tag string.
 */
export const DEFAULT_RISK_BANDS = [
  { tag: 'Low Risk', min: 0, max: 50 },
  { tag: 'Medium Risk', min: 50, max: 80 },
  { tag: 'High Risk', min: 80, max: 100 },
];

/** Lowest possible score a band may cover. */
export const MIN_RISK_SCORE = 0;
/** Highest possible score a band may cover. */
export const MAX_RISK_SCORE = 100;

/** Saved bands -> editor rows (strings, so a half typed number stays typable). */
export const toRiskBandRows = (bands) =>
  (Array.isArray(bands) && bands.length ? bands : DEFAULT_RISK_BANDS)
    .filter(Boolean)
    .map((band) => ({
      tag: band?.tag == null ? '' : String(band.tag),
      min: band?.min == null ? '' : String(band.min),
      max: band?.max == null ? '' : String(band.max),
    }))
    .sort((a, b) => Number(a.min) - Number(b.min));

/** Editor rows -> the `{ tag, min, max }` payload the API stores. */
export const toRiskBandPayload = (rows) =>
  (rows || [])
    .map((row) => ({
      tag: String(row?.tag || '').trim(),
      min: Number(row?.min),
      max: Number(row?.max),
    }))
    .sort((a, b) => a.min - b.min);

/**
 * Why the rows can't be saved, or null when they can. The same rules the API
 * enforces, checked here so a bad edit doesn't need a round trip to be told:
 *
 * - every band needs a tag and a real, in-range `min` < `max`
 * - tags have to be distinct, since the tag is what the report displays
 * - bands must not genuinely overlap -- touching at a boundary is fine and is
 *   how the default itself is shaped, but `[0,60]` with `[50,100]` is rejected
 */
export const validateRiskBands = (rows) => {
  const list = rows || [];
  if (!list.length) return 'Add at least one band.';

  for (let i = 0; i < list.length; i += 1) {
    const row = list[i];
    const tag = String(row?.tag || '').trim();
    const min = Number(row?.min);
    const max = Number(row?.max);
    if (!tag) return 'Every band needs a label.';
    if (
      String(row?.min ?? '').trim() === '' ||
      String(row?.max ?? '').trim() === '' ||
      !Number.isFinite(min) ||
      !Number.isFinite(max)
    ) {
      return `"${tag}" needs a numeric from and to score.`;
    }
    if (min < MIN_RISK_SCORE || max > MAX_RISK_SCORE) {
      return `"${tag}" has to stay within ${MIN_RISK_SCORE} - ${MAX_RISK_SCORE}.`;
    }
    if (min >= max) return `"${tag}" needs its from score below its to score.`;
  }

  const tags = list.map((row) =>
    String(row?.tag || '')
      .trim()
      .toLowerCase(),
  );
  if (new Set(tags).size !== tags.length) {
    return 'Two bands share the same label.';
  }

  const sorted = toRiskBandPayload(list);
  for (let i = 1; i < sorted.length; i += 1) {
    // Sorted by `min`, so the only way to overlap is to start before the
    // previous band ended. Starting exactly where it ended is the touching
    // boundary the backend allows.
    if (sorted[i].min < sorted[i - 1].max) {
      return `"${sorted[i - 1].tag}" and "${sorted[i].tag}" overlap.`;
    }
  }

  return null;
};

/**
 * Gaps between the bands, as `[from, to]` pairs. Not an error -- the API
 * accepts them -- but a score landing in one gets no tag at all, so the editor
 * warns about them.
 */
export const getRiskBandGaps = (rows) => {
  const sorted = toRiskBandPayload(rows).filter(
    (band) => Number.isFinite(band.min) && Number.isFinite(band.max),
  );
  if (!sorted.length) return [];
  const gaps = [];
  if (sorted[0].min > MIN_RISK_SCORE)
    gaps.push([MIN_RISK_SCORE, sorted[0].min]);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].min > sorted[i - 1].max) {
      gaps.push([sorted[i - 1].max, sorted[i].min]);
    }
  }
  const last = sorted[sorted.length - 1];
  if (last.max < MAX_RISK_SCORE) gaps.push([last.max, MAX_RISK_SCORE]);
  return gaps;
};
